import React, { useState } from 'react';
import { CATEGORIES, HIGGS_RELATIONS } from '../data';
import { RUACH_PARTICLES } from '../ruach';
import ShareableCard from './ShareableCard';

export default function ParticleDetail({ particle, onClose, mode }) {
  const [showCard, setShowCard] = useState(false);
  if (!particle) return null;
  const isRuach = mode === 'ruach';
  const cat = CATEGORIES[particle.category] || CATEGORIES.boson;
  const rel = HIGGS_RELATIONS[particle.higgsRelation] || HIGGS_RELATIONS.none;
  const ruach = RUACH_PARTICLES[particle.id];
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  const facts = [
    { label: 'Symbol', value: particle.symbol },
    { label: 'Mass', value: particle.massLabel },
    { label: 'Spin', value: particle.spin },
    { label: 'Charge', value: particle.charge },
    { label: 'Category', value: cat.label },
    ...(particle.generation ? [{ label: 'Generation', value: particle.generation }] : []),
  ];

  return (
    <>
      {showCard && <ShareableCard particle={particle} mode={mode} onClose={() => setShowCard(false)} />}

      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 'min(440px, 100vw)',
        background: isRuach ? 'rgba(12,8,4,0.97)' : 'rgba(8,8,16,0.97)',
        borderLeft: `1px solid ${isRuach ? 'rgba(255,160,60,0.15)' : 'rgba(255,255,255,0.08)'}`,
        overflowY: 'auto', zIndex: 100,
        animation: 'slideIn 0.2s ease-out', backdropFilter: 'blur(20px)',
      }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{
          padding: '2rem 1.5rem 1.5rem',
          borderBottom: `1px solid ${isRuach ? 'rgba(255,160,60,0.1)' : 'rgba(255,255,255,0.06)'}`,
          position: 'sticky', top: 0,
          background: isRuach ? 'rgba(12,8,4,0.98)' : 'rgba(8,8,16,0.98)', zIndex: 1,
        }}>
          {/* Close + Share buttons */}
          <div style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', display: 'flex', gap: '6px' }}>
            <button onClick={() => setShowCard(true)} style={{
              background: `${accentColor}15`, border: `1px solid ${accentColor}40`,
              color: accentColor, cursor: 'pointer', borderRadius: '6px',
              padding: '4px 10px', fontSize: '0.65rem', fontFamily: 'Space Mono',
              letterSpacing: '0.06em',
            }}>
              Share card
            </button>
            <button onClick={onClose} style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', borderRadius: '6px',
              width: '32px', height: '32px', fontSize: '1.1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>

          <div style={{
            display: 'inline-block', fontSize: '0.65rem', letterSpacing: '0.12em',
            textTransform: 'uppercase', color: cat.color, fontFamily: 'Space Mono',
            marginBottom: '0.5rem', background: cat.bg, padding: '2px 8px',
            borderRadius: '3px', border: `1px solid ${cat.border}`,
          }}>{cat.label}</div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: '#fff', margin: 0 }}>
              {isRuach && ruach ? ruach.ruachName : particle.name}
            </h2>
            <span style={{ fontFamily: 'Space Mono', fontSize: '1rem', color: cat.color, opacity: 0.8 }}>{particle.symbol}</span>
          </div>
          {isRuach && ruach?.ruachHebrew && (
            <div style={{ fontSize: '1.1rem', color: accentColor, opacity: 0.7, fontFamily: 'serif', marginTop: '0.3rem' }}>
              {ruach.ruachHebrew}
            </div>
          )}
        </div>

        {/* Higgs relation */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ padding: '0.75rem 1rem', borderRadius: '8px', background: `${rel.color}10`, border: `1px solid ${rel.color}35` }}>
            <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: rel.color, fontFamily: 'Space Mono', marginBottom: '0.3rem' }}>
              {isRuach ? 'Ruach field relationship' : 'Higgs field relationship'}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: rel.color, fontFamily: 'Syne' }}>
              {isRuach && ruach ? ruach.couplingNote : rel.label}
            </div>
            {!isRuach && particle.couplingStrength > 0 && (
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem', fontFamily: 'Space Mono' }}>
                Coupling: {(particle.couplingStrength * 100).toFixed(2)}% of top quark
              </div>
            )}
          </div>
        </div>

        {/* Facts */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {facts.map(f => (
              <div key={f.label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '6px', padding: '0.6rem 0.75rem' }}>
                <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.3)', fontFamily: 'Space Mono', marginBottom: '0.2rem' }}>{f.label}</div>
                <div style={{ fontSize: '0.83rem', color: '#fff', fontFamily: 'Space Mono', fontWeight: 700 }}>{f.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize: '0.87rem', lineHeight: 1.8, color: 'rgba(255,255,255,0.72)', margin: 0 }}>
            {isRuach && ruach ? ruach.description : particle.description}
          </p>
        </div>

        {/* Detail */}
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.22)', fontFamily: 'Space Mono', marginBottom: '0.75rem' }}>
            {isRuach ? 'Deeper witness' : 'Deeper context'}
          </div>
          <p style={{ fontSize: '0.82rem', lineHeight: 1.78, color: 'rgba(255,255,255,0.42)', margin: 0, fontStyle: 'italic' }}>
            {isRuach && ruach ? ruach.detail : particle.detail}
          </p>
        </div>
      </div>
    </>
  );
}
